import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import FieldTextInput from '../FieldTextInput/FieldTextInput';
import IconCollection from '../IconCollection/IconCollection';
import { Button, Chevron, FieldCheckbox } from '../../components';
import classNames from 'classnames';
import css from './ListingReviewSummaryField.module.css';

function renderStars(rating) {
    const fullStars = Math.floor(rating);
    const halfStar = rating % 1 >= 0.5;
    const stars = [];
    for (let i = 0; i < 5; i++) {
        if (i < fullStars) stars.push(<span key={i} className={css.star}>★</span>);
        else if (i === fullStars && halfStar) stars.push(<span key={i} className={css.star}>☆</span>);
        else stars.push(<span key={i} className={css.starEmpty}>★</span>);
    }
    return stars;
}

const ListingReviewSummaryField = ({
    name,
    sectionTitle,
    label,
    placeholder,
    value = {},
    onRegenerate,
    regenerateDisabled,
    helperText,
    required,
    maxLength,
    showRegenerate = true,
    showChevron = true,
    showCredits = true,
    showToggle = true,
    toggleValue,
    enabled,
    creditsUsed,
    maxCredits,
    onCreditsInfoClick,
    generateInProgress,
    generateError,
    ...rest
}) => {
    if (!value || !value?.rating) return null;
    const [expanded, setExpanded] = useState(true);
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 767);

    useEffect(() => {
        const handleResize = () => {
            if (typeof window !== 'undefined') {
                setIsMobile(window.innerWidth <= 767);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const charCount = value ? value.length : 0;

    const hiddenClass = !expanded ? css.bodyCollapsed : '';
    return (
        <div className={css.root}>
            <div className={css.header}>
                <div className={css.headerTitle}>
                    <span>{sectionTitle}</span>
                </div>
                <div className={css.headerActions}>

                    {!isMobile && showRegenerate && (
                        <Button
                            variant="warning"
                            type="button"
                            className={css.regenerateBtn}
                            size={"sm"}
                            onClick={onRegenerate}
                            disabled={regenerateDisabled}
                        >
                            <span className={classNames(css.regenerateIcon, generateInProgress && css.spinner)}>
                                <IconCollection name="icon_regenrate" width={16} height={16} />
                            </span> Regenerate
                        </Button>
                    )}
                    {showToggle && (
                        <div className={css.toggleWrapper}>
                            <label className={css.toggleLabel}>
                                <FieldCheckbox
                                    id={`${name}Enabled`}
                                    name={`${name}Enabled`}
                                    value={toggleValue}
                                    label=""
                                    className={css.toggleInput}
                                />
                                <span className={classNames(css.toggleSlider, { [css.enabled]: enabled })}></span>
                            </label>
                            <span className={css.toggleLabelText}>Enabled</span>
                        </div>
                    )}
                    {showChevron && (
                        <button
                            type="button"
                            className={css.toggleBtn}
                            onClick={() => setExpanded(e => !e)}
                            aria-label={expanded ? 'Collapse' : 'Expand'}
                        >
                            <Chevron expanded={expanded} />
                        </button>
                    )}
                </div>

            </div>
            <span className={css.subTitle}>{label}{required && <span className={css.required}>*</span>}</span>
            <div className={expanded ? css.body : css.bodyCollapsed}>
                <h4 className={css.averageTitle}>
                    Average Rating
                </h4>

                <div className={css.ratingRow}>
                    <span className={css.stars}>{renderStars(value.rating)}</span>
                    <span className={css.ratingValue}>{value.rating.toFixed(1)}</span>
                    <span className={css.ratingText}>(based on public reviews)</span>
                </div>
                <div className={css.note}>

                    Rating pulled from trusted public sources.
                </div>
                <div className={css.inputRow}>
                    <FieldTextInput
                        name={`${name}.content`}
                        placeholder={placeholder}
                        maxLength={maxLength}
                        helperText={helperText}
                        required={required}
                        type="textarea"
                        {...rest}
                    />
                    <span className={css.charCounter}>{charCount}/{maxLength}</span>
                </div>


            </div>
            <div className={classNames(css.mobileActions, hiddenClass)}>
                {isMobile && showRegenerate && (
                    <Button
                        variant="warning"
                        type="button"
                        className={css.regenerateBtn}
                        size={"sm"}
                        onClick={onRegenerate}
                        disabled={regenerateDisabled}
                    >
                        <span className={classNames(css.regenerateIcon, generateInProgress && css.spinner)}>
                            <IconCollection name="icon_regenrate" width={16} height={16} />
                        </span> Regenerate
                    </Button>
                )}
                {showCredits && <div className={css.creditsRow}>
                    <span className={css.creditsText}>{creditsUsed}/{maxCredits} credits used</span>
                    {typeof onCreditsInfoClick === 'function' && (
                        <button type="button" className={css.creditsInfoBtn} onClick={onCreditsInfoClick}>
                            <IconCollection name="info_icon" width={16} height={16} />
                        </button>
                    )}
                </div>}
            </div>
        </div>
    );
};

ListingReviewSummaryField.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    value: PropTypes.string,
    onRegenerate: PropTypes.func,
    regenerateDisabled: PropTypes.bool,
    helperText: PropTypes.string,
    required: PropTypes.bool,
    maxLength: PropTypes.number,
    showRegenerate: PropTypes.bool,
    showChevron: PropTypes.bool,
    showCredits: PropTypes.bool,
    showToggle: PropTypes.bool,
    creditsUsed: PropTypes.number,
    maxCredits: PropTypes.number,
    onCreditsInfoClick: PropTypes.func,
    rating: PropTypes.number.isRequired,
};

export default ListingReviewSummaryField; 